import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { DataTable } from './common/DataTable';
import toast from 'react-hot-toast';
import {
  Search,
  Filter,
  CheckCircle,
  RefreshCw,
  Gift,
  Upload,
  Download,
  XCircle,
  Eye,
  CreditCard,
  User,
  Calendar,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { downloadCSV } from '../lib/exportUtils';
import { ColumnDef } from '@tanstack/react-table';

interface Transaction {
  id: string;
  _id: string;
  type: 'donation' | 'pooja' | 'event' | 'sponsorship';
  amount: number;
  currency: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  temple?: { name: string };
  itemName?: string; // Pooja name, Event name, Sponsorship title, or "Donation"
  status: string;
  paymentStatus: string;
  createdAt: string;
  raw: any; // Store original object for detail modal
}

interface TransactionFilters {
  status: 'all' | 'completed' | 'failed' | 'refunded';
  type: 'all' | 'donation' | 'pooja' | 'event' | 'sponsorship';
  dateRange: 'all' | 'today' | 'week' | 'month';
  search: string;
}

const DonationsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<TransactionFilters>({
    status: 'all',
    type: 'all',
    dateRange: 'all',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Bulk import states (specifically for donations)
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importTempleId, setImportTempleId] = useState('');
  const [temples, setTemples] = useState<any[]>([]);

  const fetchTemples = async () => {
    try {
      const response = await adminApiClient.getTemples();
      if (response.success && response.data) {
        setTemples(response.data.temples);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAllTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const [donationRes, poojaRes, eventRes, sponsorRes] = await Promise.all([
        adminApiClient.getDonations({ limit: 5000 }),
        adminApiClient.getPoojaBookings({ limit: 5000 }),
        adminApiClient.getAllEventBookings({ limit: 5000 }),
        adminApiClient.getAllSponsorshipBookings({ limit: 5000 }),
      ]);

      const list: Transaction[] = [];

      // Mapped Donations
      (donationRes.data?.donations || []).forEach((d: any) => {
        if (d.status === 'pending') return;
        list.push({
          id: d._id,
          _id: d._id,
          type: 'donation',
          amount: d.amount || 0,
          currency: d.currency || 'USD',
          contactName: d.donorName || d.donor?.name || 'Anonymous',
          contactEmail: d.donorEmail || d.donor?.email,
          contactPhone: d.donor?.phone,
          temple: d.temple,
          itemName: d.purpose || 'General Donation',
          status: d.status,
          paymentStatus: d.status === 'completed' ? 'paid' : d.status,
          createdAt: d.createdAt,
          raw: d,
        });
      });

      // Mapped Poojas
      (poojaRes.data?.bookings || []).forEach((b: any) => {
        if (b.paymentStatus === 'pending') return;
        list.push({
          id: b._id,
          _id: b._id,
          type: 'pooja',
          amount: b.amount || 0,
          currency: b.currency || 'USD',
          contactName: b.contactInfo?.name || b.user?.name || 'N/A',
          contactEmail: b.contactInfo?.email || b.user?.email,
          contactPhone: b.contactInfo?.phone || b.user?.phone,
          temple: b.temple,
          itemName: b.pooja?.name || 'Pooja',
          status: b.paymentStatus === 'paid' ? 'completed' : b.paymentStatus,
          paymentStatus: b.paymentStatus,
          createdAt: b.createdAt,
          raw: b,
        });
      });

      // Mapped Events
      (eventRes.data?.bookings || []).forEach((b: any) => {
        if (b.paymentStatus === 'pending') return;
        list.push({
          id: b._id,
          _id: b._id,
          type: 'event',
          amount: b.amount || 0,
          currency: b.currency || 'USD',
          contactName: b.user?.name || 'N/A',
          contactEmail: b.user?.email,
          contactPhone: b.user?.phone,
          temple: b.temple,
          itemName: b.event?.name || 'Event',
          status: b.paymentStatus === 'paid' ? 'completed' : b.paymentStatus,
          paymentStatus: b.paymentStatus,
          createdAt: b.createdAt,
          raw: b,
        });
      });

      // Mapped Sponsorships
      (sponsorRes.data?.bookings || []).forEach((b: any) => {
        if (b.paymentStatus === 'pending') return;
        list.push({
          id: b._id,
          _id: b._id,
          type: 'sponsorship',
          amount: b.amount || 0,
          currency: b.currency || 'USD',
          contactName: b.user?.name || 'N/A',
          contactEmail: b.user?.email,
          contactPhone: b.user?.phone,
          temple: b.temple,
          itemName: b.sponsorship?.title || 'Sponsorship',
          status: b.paymentStatus === 'paid' ? 'completed' : b.paymentStatus,
          paymentStatus: b.paymentStatus,
          createdAt: b.createdAt,
          raw: b,
        });
      });

      list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setTransactions(list);
    } catch (err) {
      toast.error('Error fetching transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllTransactions();
    fetchTemples();
  }, [fetchAllTransactions]);

  const filteredTransactions = transactions.filter((t) => {
    const s = (filters.search || '').toLowerCase();
    const matchesSearch = !s ||
      (t.contactName || '').toLowerCase().includes(s) ||
      (t.contactEmail || '').toLowerCase().includes(s) ||
      (t.itemName || '').toLowerCase().includes(s) ||
      (t.id || '').toLowerCase().includes(s) ||
      (t.temple?.name || '').toLowerCase().includes(s);

    const matchesType = filters.type === 'all' || t.type === filters.type;
    const matchesStatus = filters.status === 'all' || t.status === filters.status;

    let matchesDate = true;
    if (filters.dateRange !== 'all') {
      const date = new Date(t.createdAt);
      const now = new Date();
      const daysDiff = (now.getTime() - date.getTime()) / (1000 * 3600 * 24);
      if (filters.dateRange === 'today') matchesDate = daysDiff < 1;
      if (filters.dateRange === 'week') matchesDate = daysDiff < 7;
      if (filters.dateRange === 'month') matchesDate = daysDiff < 30;
    }

    return matchesSearch && matchesType && matchesStatus && matchesDate;
  });

  const stats = {
    totalRevenue: filteredTransactions
      .filter(t => t.status === 'completed')
      .reduce((acc, curr) => acc + (curr.amount || 0), 0),
    totalCount: filteredTransactions.length,
    successfulCount: filteredTransactions.filter(t => t.status === 'completed').length,
    failedCount: filteredTransactions.filter(t => t.status === 'failed' || t.status === 'refunded').length,
  };

  const handleExportCSV = () => {
    const rows = filteredTransactions.map(t => ({
      ID: t.id,
      Type: t.type,
      Item: t.itemName,
      Customer: t.contactName,
      Email: t.contactEmail,
      Temple: t.temple?.name,
      Amount: t.amount,
      Currency: t.currency,
      Status: t.status,
      Date: new Date(t.createdAt).toLocaleString(),
    }));
    downloadCSV(rows, `transactions_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success('Transactions exported');
  };

  const handleBulkImport = async () => {
    if (!importFile || !importTempleId) {
      toast.error('Please select a temple and a file');
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        let dataToImport = [];
        if (importFile.name.endsWith('.json')) {
          dataToImport = JSON.parse(text);
        } else if (importFile.name.endsWith('.csv')) {
          // Quick CSV parse for demonstration
          const lines = text.split('\n');
          const headers = lines[0].split(',').map(h => h.trim());
          for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue;
            const values = lines[i].split(',').map(v => v.trim());
            const obj: any = {};
            headers.forEach((h, idx) => {
              let val: any = values[idx];
              if (val === 'true') val = true;
              if (val === 'false') val = false;
              if (!isNaN(Number(val)) && val !== '') val = Number(val);
              obj[h] = val;
            });
            dataToImport.push(obj);
          }
        }

        const loadingToast = toast.loading('Importing donations...');
        const res = await adminApiClient.bulkImportDonations(importTempleId, dataToImport);
        if (res.success) {
          toast.success(res.message, { id: loadingToast });
          setShowImportModal(false);
          setImportFile(null);
          setImportTempleId('');
          fetchAllTransactions();
        } else {
          toast.error(res.message || 'Import failed', { id: loadingToast });
        }
      } catch (err: any) {
        toast.error('Error parsing file', { id: 'import' });
      }
    };
    reader.readAsText(importFile);
  };

  const columns: ColumnDef<Transaction>[] = [
    {
      accessorKey: 'type',
      header: 'Type',
      cell: (info) => {
        const t = info.getValue<string>();
        const colors: any = {
          donation: 'bg-blue-100 text-blue-700',
          pooja: 'bg-orange-100 text-orange-700',
          event: 'bg-purple-100 text-purple-700',
          sponsorship: 'bg-indigo-100 text-indigo-700',
        };
        return <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${colors[t] || 'bg-gray-100'}`}>{t}</span>;
      },
    },
    {
      accessorKey: 'itemName',
      header: 'Item/Purpose',
      cell: (info) => <span className="text-sm font-medium text-gray-700">{info.getValue<string>()}</span>,
    },
    {
      accessorKey: 'contactName',
      header: 'Customer/Donor',
      cell: (info) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{info.getValue<string>()}</span>
          <span className="text-xs text-gray-500">{info.row.original.contactEmail}</span>
        </div>
      ),
    },
    {
      accessorKey: 'temple',
      header: 'Temple',
      cell: (info) => <span className="text-sm text-gray-600">{info.getValue<any>()?.name || 'N/A'}</span>,
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: (info) => (
        <div className="font-bold text-gray-900">
          {info.row.original.currency} {info.getValue<number>().toLocaleString()}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => {
        const s = info.getValue<string>();
        const isSuccess = s === 'completed';
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${isSuccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {s}
          </span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: (info) => <span className="text-xs text-gray-500">{new Date(info.getValue<string>()).toLocaleDateString()}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <button
          onClick={() => setSelectedTransaction(info.row.original)}
          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          title="View Details"
        >
          <Eye size={18} />
        </button>
      ),
    },
  ];

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transaction Records</h1>
          <p className="text-sm text-gray-500">Unified view of all completed and failed transactions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchAllTransactions}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Upload size={18} /> Bulk Import Donations
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <Download size={18} /> Export CSV
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
          <p className="text-2xl font-bold text-gray-900">${stats.totalRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500 mb-1">Total Transactions</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalCount}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500 mb-1 text-green-600">Successful</p>
          <p className="text-2xl font-bold text-green-600">{stats.successfulCount}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500 mb-1 text-red-600">Failed/Refunded</p>
          <p className="text-2xl font-bold text-red-600">{stats.failedCount}</p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by customer, item, temple, or ID..."
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-primary)]/20 outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${showFilters ? 'bg-gray-50 border-gray-300' : 'hover:bg-gray-50'}`}
          >
            <Filter size={18} /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
              <select
                value={filters.status}
                onChange={e => setFilters({ ...filters, status: e.target.value as any })}
                className="w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Type</label>
              <select
                value={filters.type}
                onChange={e => setFilters({ ...filters, type: e.target.value as any })}
                className="w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
              >
                <option value="all">All Types</option>
                <option value="donation">Donations</option>
                <option value="pooja">Pooja Bookings</option>
                <option value="event">Event Bookings</option>
                <option value="sponsorship">Sponsorships</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Date Range</label>
              <select
                value={filters.dateRange}
                onChange={e => setFilters({ ...filters, dateRange: e.target.value as any })}
                className="w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <DataTable
          data={filteredTransactions}
          columns={columns}
          loading={loading}
          enableClientSidePagination={true}
        />
      </div>

      {/* Detail Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl animate-in zoom-in duration-200">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Transaction Details</h2>
                <p className="text-xs font-mono text-gray-500 mt-0.5">#{selectedTransaction.id}</p>
              </div>
              <button
                onClick={() => setSelectedTransaction(null)}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="p-6 space-y-8">
              {/* Header Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Amount Paid</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedTransaction.currency} {selectedTransaction.amount.toLocaleString()}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold uppercase ${selectedTransaction.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {selectedTransaction.status === 'completed' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                    {selectedTransaction.status}
                  </span>
                </div>
              </div>

              {/* Detailed Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 uppercase tracking-widest border-b pb-2">
                    <User size={16} /> Customer Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-400">Full Name</p>
                      <p className="text-sm font-medium text-gray-800">{selectedTransaction.contactName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Email Address</p>
                      <p className="text-sm font-medium text-gray-800">{selectedTransaction.contactEmail || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Phone Number</p>
                      <p className="text-sm font-medium text-gray-800">{selectedTransaction.contactPhone || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 uppercase tracking-widest border-b pb-2">
                    <Gift size={16} /> Item Details
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-400">Type</p>
                      <p className="text-sm font-medium text-gray-800 capitalize">{selectedTransaction.type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Item/Purpose</p>
                      <p className="text-sm font-medium text-gray-800">{selectedTransaction.itemName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Temple</p>
                      <p className="text-sm font-medium text-gray-800">{selectedTransaction.temple?.name || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 uppercase tracking-widest border-b pb-2">
                  <CreditCard size={16} /> Payment Info
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Date</p>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                      <Calendar size={14} className="text-gray-400" />
                      {new Date(selectedTransaction.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Time</p>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                      <Clock size={14} className="text-gray-400" />
                      {new Date(selectedTransaction.createdAt).toLocaleTimeString()}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Transaction ID</p>
                    <p className="text-sm font-mono text-gray-800 break-all">{selectedTransaction.raw?.paymentIntentId || selectedTransaction.raw?.bookingId || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={() => setSelectedTransaction(null)}
                className="px-6 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal (Existing) */}
      {showImportModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Bulk Import Donations</h2>
              <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:bg-gray-100 p-1 rounded-full transition-colors"><XCircle size={24} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Temple</label>
                <select value={importTempleId} onChange={e => setImportTempleId(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20">
                  <option value="">Select Temple</option>
                  {temples.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload File (.csv, .json)</label>
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={e => setImportFile(e.target.files ? e.target.files[0] : null)}
                  className="w-full px-3 py-2 border border-dashed rounded-lg bg-gray-50"
                />
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button onClick={() => setShowImportModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button
                  onClick={handleBulkImport}
                  disabled={!importFile || !importTempleId}
                  className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg disabled:opacity-50"
                >
                  Start Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationsPage;
